import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { asPlaceholder } from "../../i18n/filterLabels";
import {
  Col,
  Container,
  Row,
  Table,
} from "react-bootstrap";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import DateInput from "../../components/ui/DateInput";
import { Button, Pagination } from "../../components/ui";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import warehouseIcon from "../../media/icons8-hierarchy.png";
import GetData from "../../services/getData";
import filterIcon from "../../media/filter_icon.png";

function WarehouseLogs() {
  const { t } = useTranslation("warehouse");
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");
  const [selectedService, setSelectedService] = useState("all");
  const [selectedDirection, setSelectedDirection] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [isFiltersActive, setIsFiltersActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });

  useEffect(() => {
    document.title = t("logs.title");
  }, [t]);

  const getFieldValue = (field, fallback = "") => {
    if (field && typeof field === "object" && "value" in field) {
      return field.value;
    }
    return field ?? fallback;
  };

  const [users, setUsers] = useState([]);

  const loadLogs = async () => {
    const timer = setTimeout(() => setLoading(true), 300);
    setError(null);
    try {
      const data = await GetData({
        module: "warehouse",
        block: "logs",
        search: searchTerm.trim(),
        id_warehouse: selectedWarehouse === "all" ? "" : selectedWarehouse,
        id_service: selectedService === "all" ? "" : selectedService,
        direction: selectedDirection === "all" ? "" : selectedDirection,
        id_user: selectedUser === "all" ? "" : selectedUser,
        date_from: dateFrom || "",
        date_to: dateTo || "",
        page: pagination.page,
        limit: pagination.limit,
      });

      const result = data.result || data.request?.result || [];
      const warehousesData =
        data.warehouses || data.request?.warehouses || [];
      const servicesData = data.services || data.request?.services || [];
      const usersData = data.users || data.request?.users || [];
      const paginationData =
        data.pagination || data.request?.pagination || null;

      setItems(result);
      setWarehouses(warehousesData);
      setServices(servicesData);
      setUsers(usersData);

      if (paginationData) {
        setPagination((prev) => ({
          ...prev,
          page: paginationData.page ?? prev.page,
          limit: paginationData.limit ?? prev.limit,
          total: paginationData.total ?? prev.total,
          pages: paginationData.pages ?? prev.pages,
        }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Ошибка загрузки истории склада:", err);
      setError(err.message || t("logs.errors.loadFailed"));
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      loadLogs();
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    selectedWarehouse,
    selectedService,
    selectedDirection,
    selectedUser,
    dateFrom,
    dateTo,
    pagination.page,
  ]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleServiceChange = (e) => {
    setSelectedService(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDirectionChange = (e) => {
    setSelectedDirection(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUserChange = (e) => {
    setSelectedUser(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateToChange = (e) => {
    setDateTo(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Есть ли выбранный фильтр: подсвечиваем такие поля, кнопки «Сбросить» больше нет.
  // Диапазон дат считается выбранным, если заполнено хотя бы одно из двух полей.
  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    selectedWarehouse !== "all" ||
    selectedService !== "all" ||
    selectedDirection !== "all" ||
    selectedUser !== "all" ||
    dateFrom !== "" ||
    dateTo !== "";

  const totalPages =
    pagination.pages ||
    (pagination.total && pagination.limit
      ? Math.ceil(pagination.total / pagination.limit)
      : 1);

  const handlePageClick = (pageNumber) => {
    setPagination((prev) => ({ ...prev, page: pageNumber }));
  };

  const renderPageButtons = () => (
    <Pagination
      onChange={handlePageClick}
      page={pagination.page}
      totalPages={totalPages}
    />
  );

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={warehouseIcon}
                title={t("logs.title")}
                showStatus={false}
                status=""
              />
            }
            buttons={[]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""} ${
                    hasActiveFilters ? "has-active-filters" : ""
                  }`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">{t("filters.title", { ns: "common" })}</div>
                    </div>
                    <Button
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                      size="sm"
                      variant="secondary">
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </Button>
                  </div>
                  <div className="search-section">
                    <input
                      type="text"
                      placeholder={t("logs.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className={`search-input ${searchTerm.trim() ? "is-filter-active" : ""}`}
                    />
                  </div>
                  <div className="filters-section">
                    <div
                      className={`filter-group ${selectedWarehouse !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          selectedWarehouse === "all" ? "is-placeholder" : ""
                        }
                        value={selectedWarehouse}
                        onChange={handleWarehouseChange}
                      >
                        <option value="all">
                          {asPlaceholder(t("logs.warehouseLabel"))}
                        </option>
                        {warehouses.map((warehouse) => (
                          <option
                            key={warehouse?.id?.value || warehouse?.id}
                            value={warehouse?.id?.value || warehouse?.id}
                          >
                            {warehouse?.name?.value || warehouse?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      className={`filter-group ${selectedService !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          selectedService === "all" ? "is-placeholder" : ""
                        }
                        value={selectedService}
                        onChange={handleServiceChange}
                      >
                        <option value="all">
                          {asPlaceholder(t("logs.serviceLabel"))}
                        </option>
                        {services.map((service) => (
                          <option
                            key={service?.id?.value || service?.id}
                            value={service?.id?.value || service?.id}
                          >
                            {service?.name?.value || service?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      className={`filter-group ${selectedDirection !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          selectedDirection === "all" ? "is-placeholder" : ""
                        }
                        value={selectedDirection}
                        onChange={handleDirectionChange}
                      >
                        <option value="all">
                          {asPlaceholder(t("logs.directionLabel"))}
                        </option>
                        <option value="1">{t("logs.income")}</option>
                        <option value="0">{t("logs.writeoff")}</option>
                      </select>
                    </div>
                    <div
                      className={`filter-group ${selectedUser !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          selectedUser === "all" ? "is-placeholder" : ""
                        }
                        value={selectedUser}
                        onChange={handleUserChange}
                      >
                        <option value="all">
                          {asPlaceholder(t("logs.userLabel"))}
                        </option>
                        {users.map((user) => (
                          <option
                            key={user?.id?.value || user?.id}
                            value={user?.id?.value || user?.id}
                          >
                            {user?.name?.value || user?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      className={`filter-group filter-range ${
                        dateFrom !== "" || dateTo !== "" ? "is-filter-active" : ""
                      }`}
                    >
                      <div className="filter-range-inputs">
                        <DateInput
                          value={dateFrom}
                          hint={asPlaceholder(t("logs.dateLabel"))}
                          onChange={handleDateFromChange}
                        />
                        <span className="range-separator">—</span>
                        <DateInput
                          value={dateTo}
                          hint={asPlaceholder(t("logs.dateLabel"))}
                          onChange={handleDateToChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">{t("error", { ns: "common", message: error })}</div>
                ) : loading ? (
                  <div>{t("state.loading", { ns: "common" })}</div>
                ) : items.length === 0 ? (
                  <div className="no-patients">{t("logs.notFound")}</div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("logs.colDate")}</th>
                        <th>{t("logs.colWarehouse")}</th>
                        <th>{t("logs.colService")}</th>
                        <th>{t("logs.colUnit")}</th>
                        <th>{t("logs.colDirection")}</th>
                        <th>{t("logs.colCount")}</th>
                        <th>{t("logs.colUser")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr
                          key={item?.id?.value || item?.id}
                          className="table-item-link"
                        >
                          <td>{getFieldValue(item?.date, "")}</td>
                          <td>{getFieldValue(item?.warehouse_name, "")}</td>
                          <td>{getFieldValue(item?.service_name, "")}</td>
                          <td>{getFieldValue(item?.unit_name, "")}</td>
                          <td>{getFieldValue(item?.direction_name, "")}</td>
                          <td>{getFieldValue(item?.count, 0)}</td>
                          <td>{getFieldValue(item?.user_name, "")}</td>
                        </tr>
                      ))}
                      <tr className="table-summary">
                        <td colSpan={7} className="summary-cell">
                          {t("shared.foundRecords", { count: pagination.total || items.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
                {items.length > 0 && totalPages > 1 && (
                  <div className="pagination-controls mt-3 d-flex justify-content-between align-items-center">
                    <span>
                      {t("shared.pageOf", { page: pagination.page, total: totalPages })}
                    </span>
                    <div>{renderPageButtons()}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default WarehouseLogs;

